import { AttributeGroupDraft } from '@features/catalog/models/product-form-draft.models';
import { catalogLabelExistsIn } from '@features/catalog/utils/catalog-label-normalize';

export type AttributeGroupNameConflictKind = 'catalog-duplicate' | 'sibling-duplicate';

export interface AttributeGroupNameConflict {
  kind: AttributeGroupNameConflictKind;
  groupTempId: string;
  name: string;
}

const BACKEND_ATTRIBUTE_NAME_TAKEN_MESSAGE = 'Ya existe un atributo con ese nombre';
const BACKEND_ATTRIBUTE_DUPLICATE_IN_PRODUCT_MESSAGE =
  'No puede haber dos atributos con el mismo nombre en este producto';

/** Grupos inline (sin definitionId) con nombre duplicado en catálogo o entre sí. */
export function findAttributeGroupNameConflicts(
  groups: readonly AttributeGroupDraft[],
  catalogNames: readonly string[]
): AttributeGroupNameConflict[] {
  const conflicts: AttributeGroupNameConflict[] = [];
  const seenSiblingNames: string[] = [];

  for (const group of groups) {
    const name = group.name.trim();
    if (!name) {
      continue;
    }

    if (catalogLabelExistsIn(name, seenSiblingNames)) {
      conflicts.push({ kind: 'sibling-duplicate', groupTempId: group.tempId, name });
    } else {
      seenSiblingNames.push(name);
    }

    if (!group.definitionId && catalogLabelExistsIn(name, catalogNames)) {
      conflicts.push({ kind: 'catalog-duplicate', groupTempId: group.tempId, name });
    }
  }

  return conflicts;
}

export function getAttributeGroupNameConflict(
  conflicts: readonly AttributeGroupNameConflict[],
  groupTempId: string
): AttributeGroupNameConflict | null {
  return conflicts.find((conflict) => conflict.groupTempId === groupTempId) ?? null;
}

export function wouldAddAttributeGroupNameConflict(
  groups: readonly AttributeGroupDraft[],
  catalogNames: readonly string[],
  candidate: { name: string; definitionId?: string | null; tempId?: string }
): AttributeGroupNameConflict | null {
  const hypothetical: AttributeGroupDraft = {
    tempId: candidate.tempId ?? '__candidate__',
    definitionId: candidate.definitionId ?? null,
    name: candidate.name,
    values: [],
    usedForVariations: false,
  };

  const conflict = findAttributeGroupNameConflicts([...groups, hypothetical], catalogNames).find(
    (item) => item.groupTempId === hypothetical.tempId
  );

  return conflict ?? null;
}

export function hasEmptyInlineAttributeGroup(groups: readonly AttributeGroupDraft[]): boolean {
  return groups.some((group) => !group.definitionId && !group.name.trim());
}

export function buildAttributeGroupNameConflictToast(conflict: AttributeGroupNameConflict): {
  toastKey: string;
  toastParams: { name: string };
} {
  if (conflict.kind === 'catalog-duplicate') {
    return {
      toastKey: 'catalog.products.attributeGroupNameDuplicateCatalog',
      toastParams: { name: conflict.name },
    };
  }

  return {
    toastKey: 'catalog.products.attributeGroupNameDuplicate',
    toastParams: { name: conflict.name },
  };
}

/**
 * Cuando el backend rechaza un atributo inline pero el catálogo en memoria estaba desactualizado,
 * infiere el grupo conflictivo (p. ej. último inline sin definitionId).
 */
export function inferAttributeGroupNameConflictFromMutation(
  errors: readonly string[],
  groups: readonly AttributeGroupDraft[],
  catalogNames: readonly string[]
): AttributeGroupNameConflict | null {
  const hasNameTaken = errors.some((raw) =>
    raw.includes(BACKEND_ATTRIBUTE_NAME_TAKEN_MESSAGE)
  );
  const hasDuplicateInProduct = errors.some((raw) =>
    raw.includes(BACKEND_ATTRIBUTE_DUPLICATE_IN_PRODUCT_MESSAGE)
  );

  if (!hasNameTaken && !hasDuplicateInProduct) {
    return null;
  }

  const clientConflicts = findAttributeGroupNameConflicts(groups, catalogNames);
  if (clientConflicts.length) {
    return clientConflicts[0];
  }

  const inlineGroups = groups.filter(
    (group) => !group.definitionId?.trim() && group.name.trim().length > 0
  );
  const candidate = inlineGroups[inlineGroups.length - 1];
  if (!candidate) {
    return null;
  }

  return {
    kind: hasNameTaken ? 'catalog-duplicate' : 'sibling-duplicate',
    groupTempId: candidate.tempId,
    name: candidate.name.trim(),
  };
}

export function attributeGroupHasNameConflict(
  conflicts: readonly AttributeGroupNameConflict[],
  groupTempId: string
): boolean {
  return conflicts.some((conflict) => conflict.groupTempId === groupTempId);
}

export function hasAttributeGroupNameConflicts(
  groups: readonly AttributeGroupDraft[],
  catalogNames: readonly string[]
): boolean {
  return findAttributeGroupNameConflicts(groups, catalogNames).length > 0;
}
